<template>
    <h1 class="page-title">Ajouter une collection</h1>
  <FormKit
    type="form"
    :form-class="isFormSubmitted ? 'hide' : 'show'"
    @submit="submitHandler"
    :actions="false"
    #default="{ state: { valid } }"
  >
    <div class="card">
      <DataTable lazy dataKey="uuid"
                 stateStorage="local"
                 ref="dt"
                 :state-key="tableLocalStorageKey"
                 :value="tableData"
                 :paginator="true"
                 :rows="rows"
                 :rowHover="true"
                 sortMode="multiple"
                 removableSort
                 v-model:selection="selectedProducts"
                 :metaKeySelection="false"
                 :loading="isLoading"
                 :totalRecords="totalRecords"
                 :currentPage="currentPage"
                 paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                 :rowsPerPageOptions="rowsPerPageOptions"
                 currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
                 :selectAll="isSelectAllBtnChecked"
                 @select-all-change="onRowSelectAll($event)"
                 @row-select="onRowSelect($event)"
                 @row-unselect="onRowUnselect($event)"
                 @page="onPaginate($event)">
        <template #header>
          <div class="flex flex-row flex-nowrap card-header border-0 w-full">
            <div class="flex card-toolbar w-full">
              <div class="w-full">
                  <span class="p-input-icon-left w-10/12">
                      <i class="pi pi-search" />
                      <InputText type="text"  v-model="criteria" placeholder="Recherche" @keyup.enter="onSearchInputDt()" />
                  </span>
              </div>
            </div>            

           
            
          </div>
            <div class="card-toolbar w-full mb-5  mt-5">
                <FormKit
                    type="autocomplete"
                    name="autocomplete"
                    label="Rechercher une catégorie"
                    placeholder="Example: E-billet"
                    v-model="selectedOptionsCategory"
                    :options="searchCategories"
                    selection-appearance="option"
                    multiple
                >
                    <!--SELECTION SLOT-->
                    <template #selection="{ option }">
                        <div class="formkit-selection">
                            <div class="formkit-option">
                                <span>{{ option.label }}</span>
                            </div>
                        </div>

                    </template>
                    <!--/SELECTION SLOT-->
                </FormKit>
            </div>
            <div class="">                
                <p class="text-right">Nombre total de produits : <b>{{ totalRecords }}</b></p>
                <p  class="text-right">Nombre de produit sélectionné(s) : <b>{{ totalSelectedProducts }}</b> produit(s)</p> <br>
                <p  class="text-right italic" v-if="isInFilteredView">Vous êtes en vue filtrée</p>                
            </div>
        </template>
        <div class="card-body pt-0">
          <Column selectionMode="multiple"></Column>
          <Column field="name" header="Produit">
            <template #body="slotProps">
              <span> {{ slotProps.data.name }}</span>
            </template>
          </Column>
          <Column field="category" header="Catégorie">
            <template #body="slotProps">
              <span v-if="slotProps.data.categories?.length > 0" :title="formatCategoriesProduct(slotProps.data.categories)"> {{ slotProps.data.categories[0].value }} </span>
            </template>
          </Column>
          <Column field="price" header="Prix">
            <template #body="slotProps">
              <span> {{ slotProps.data.priceFormatted }} </span>
            </template>
          </Column>
        </div>
        <template #empty>Aucun résultat trouvé.</template>
      </DataTable>
    </div>
    <div class="ml-96 mt-10">
      <FormKit type="text" name="name" v-model="collectionName" label="Nom de la collection*" validation="required" :validation-messages="{ required: 'Ce champ est obligatoire.' }"/>
      <FormKit type="submit" label="Enregistrer" :disabled="!valid || totalSelectedProducts <= 0"/>
    </div>
  </FormKit>
</template>

<script lang="ts" setup>
import {computed, onBeforeMount, onMounted, ref, watch} from "vue";
import swal from "@/_helpers/swal";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import {fetchProductsForGrant, formatCategoriesProduct} from "@/_services/product/variant/productVariant";
import {createVariantProductCollection} from "@/_services/grant/variantProductCollection";
import router from "@/router";
import {
    IAddProductVariantToCollectionPlayload,
    IProductVariantForGrant
} from "@/models/product/variant/ProductVariantForGrant";
import {fetchCategoriesForDropDown} from "@/_services/category/category";

const tableLocalStorageKey = "tableSelectedProductsToAddInCollection";
const selectedProductToAddStorageKey = "selectedVariantProductsToAddInCollection";
const selectAllBtnStatusStorageKey = "selectAllVariantProductsForCollectionBtnStatus";
const isSelectAllBtnChecked = ref<boolean>(false);
const refItemsPerPage = ref<number>(0);
const isLoading = ref<boolean>(true);
const products = Array<IProductVariantForGrant>();
const tableData = ref<Array<IProductVariantForGrant>>(products);
const totalSelectedProducts = ref<number>(0);
const selectedProducts = ref(Array<IProductVariantForGrant>());
const productsOfCurrentPage = ref(Array<IProductVariantForGrant>());
const currentPage = ref<number>(0);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const criteria = ref<string>("");
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100, 250];
const selectedOptionsCategory = ref([]);
const isFormSubmitted = ref(false);
const collectionName = ref("");

onBeforeMount(() => {
    initLocalStorageData();
});

onMounted(async () => {
    await populateDataTableWithLocalStorageFilters();
});

watch(isSelectAllBtnChecked, () => {
    setIsSelectedAllBtnStatusLocalStorage(isSelectAllBtnChecked.value);
});

watch(selectedOptionsCategory, () => {
    onChangeSelectedCategory();
});

const isInFilteredView = computed(() => {
    return selectedOptionsCategory.value.length > 0 || totalSelectedProducts.value > 0;
});

const initLocalStorageData = () => {
    // Delete
    if (localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.removeItem(selectAllBtnStatusStorageKey);
    }
    if (localStorage.getItem(tableLocalStorageKey)) {
        localStorage.removeItem(tableLocalStorageKey);
    }
    if (localStorage.getItem(selectedProductToAddStorageKey)) {
        localStorage.removeItem(selectedProductToAddStorageKey);
    }

    // Create
    if (!localStorage.getItem(selectAllBtnStatusStorageKey)) {
        localStorage.setItem(selectAllBtnStatusStorageKey, 'false');
    }
    if (localStorage.getItem(tableLocalStorageKey)) {
        localStorage.removeItem(tableLocalStorageKey);
    }
    if (!localStorage.getItem(selectedProductToAddStorageKey)) {
        localStorage.setItem(selectedProductToAddStorageKey, '[]');
    }
}

async function searchCategories({ search, page, hasNextPage }) {
    const [totalCategoriesRecordsForPickListApi, categoriesForPickListApi] = await fetchCategoriesForDropDown(5, page, search);
    const hasItems = categoriesForPickListApi.length > 0;
    if (hasItems && page !== totalCategoriesRecordsForPickListApi) {
        hasNextPage();
    }

    return categoriesForPickListApi;
}

const canSubmitForm = (): boolean => {
    return !(totalSelectedProducts.value === 0 && collectionName.value === '');
}

const submitHandler = async (data) => {
  if (!canSubmitForm()) {
    swal.warning("Vous devez selectionner au moins un produit.")
  } else {
    swal
      .confirm("Êtes-vous sur de vouloir créer cette collection ?")
      .then((result) => {
        if (result.isConfirmed) {
          showSpinner();
          const params: IAddProductVariantToCollectionPlayload = {
              name: data.name,
              variantProductsUuids: getSelectedProductsUuids()
          }
          createVariantProductCollection(params).then(() => {
            swal.success("Créé !", "Vous venez de créer une collection").then(() => {
              router.push({ name: 'listVariantProductCollection'});
            });
          });
        }
      });
  
    isFormSubmitted.value = true;
  }
}

const onChangeSelectedCategory = async (): Promise<void> => {
    showSpinner();
    const search = criteria.value.trim();
    await populateDatatable(rows.value, page.value, "" != search ? search : "", selectedOptionsCategory.value);
};

const setIsSelectedAllBtnStatusLocalStorage = (status: boolean) =>{
    isSelectAllBtnChecked.value = status;
    const isSelectedAllBtnInStorageData = localStorage.getItem(selectAllBtnStatusStorageKey);
    if (!isSelectedAllBtnInStorageData) {
        localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
    } else {
        const currentStatus = JSON.parse(isSelectedAllBtnInStorageData);
        if (currentStatus !== status) {
            localStorage.setItem(selectAllBtnStatusStorageKey, status.toString());
        }
    }
}

function onSearchInputDt() {
    let search = criteria.value.trim();
    if (0 === search.length || 3 <= search.length) {
        showSpinner();
        populateDatatable(rows.value, page.value, "" != search ? search : "", selectedOptionsCategory.value);
    }
}

const populateDatatable = async (itemsPerPage: number, page: number, criteria?: string | undefined, categoriesFilter?: string[] | undefined) => {
  const [loadingDt, totalRecordsDt, tableDataDt] = await fetchProductsForGrant(itemsPerPage, page, criteria, categoriesFilter);
  totalRecords.value = totalRecordsDt;
  isLoading.value = loadingDt;
  tableData.value = tableDataDt;
  refItemsPerPage.value = itemsPerPage;
  productsOfCurrentPage.value = tableData.value;
    let selectedProductsOnCurrentPage: IProductVariantForGrant[] = [];
    selectedProductsOnCurrentPage = productsOfCurrentPage.value.filter((item: IProductVariantForGrant) =>
        getSelectedProductsToAddFromStorage().includes(item.uuid)
    );

    const totalUnSelectedProductsOnCurrentPage = getTotalUnSelectedProductsOnCurrentPage();
    isSelectAllBtnChecked.value = (itemsPerPage - totalUnSelectedProductsOnCurrentPage) === itemsPerPage;
    selectedProducts.value = selectedProductsOnCurrentPage;
}

async function populateDataTableWithLocalStorageFilters(itemsPerPage?: number, page?: number) {
  let localStorageRowPerPage = getLocalStorageRowsPerPage();
  let localStorageFisrtPageState = getLocalStorageFisrtPageState();
  let currentPage = page;
  if (!currentPage) {
    currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
  }
  let currentItemsPerPage = itemsPerPage;
  if (!currentItemsPerPage) {
    currentItemsPerPage = localStorageRowPerPage;
  }

  await populateDatatable(currentItemsPerPage as number, currentPage, criteria.value, selectedOptionsCategory.value);
}

const onPaginate = async (e) => {
    showSpinner();
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    setLocalStorageRowsPerPage(rows.value);
    await populateDataTableWithLocalStorageFilters(rows.value, page.value);
};

const showSpinner = () => {
    isLoading.value = true;
}
const hideSpinner = () => {
    isLoading.value = false;
}

const onRowSelectAll = async (event): Promise<void> => {
    if (true === event.checked) {
        isSelectAllBtnChecked.value = true;
        selectedProducts.value = productsOfCurrentPage.value;
        const productsUuidsOfCurrentPageToSelect = productsOfCurrentPage.value
            .filter((item: IProductVariantForGrant) => !getSelectedProductsToAddFromStorage().includes(item.uuid))
            .map(item => item.uuid);
        if (productsUuidsOfCurrentPageToSelect.length > 0) {
            addSelectedProductInProductsToAddInStorage(productsUuidsOfCurrentPageToSelect);
            const currentProductsToAddInStorage = getSelectedProductsToAddFromStorage();
            let newProductsToAddInStorage = currentProductsToAddInStorage;
            productsUuidsOfCurrentPageToSelect.forEach((uuid: string) => {
                if (!currentProductsToAddInStorage.includes(uuid)) {
                    newProductsToAddInStorage.push(uuid);
                }
            });
            totalSelectedProducts.value = newProductsToAddInStorage.length;
        }
    } else {
        isSelectAllBtnChecked.value = false;
        const unSelectedProductsUuidsOfCurrentPageToRemoveInStorage = productsOfCurrentPage.value.map(item => item.uuid);
        removeUnSelectedProductFromProductsToAddInStorage(unSelectedProductsUuidsOfCurrentPageToRemoveInStorage);

        const productsToAddInStorage = getSelectedProductsToAddFromStorage();
        let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !unSelectedProductsUuidsOfCurrentPageToRemoveInStorage.includes(uuid));
        totalSelectedProducts.value = newProductsToAddInStorage.length;
        selectedProducts.value = selectedProducts.value.filter((item: IProductVariantForGrant) =>
            !productsOfCurrentPage.value.map((product: IProductVariantForGrant) => product.uuid).includes(item.uuid)
        );

        hideSpinner();
    }
};

const onRowSelect = async (event: any): Promise<void> => {
    const selectedProductUuid = event.data.uuid;
    totalSelectedProducts.value++;
    addSelectedProductInProductsToAddInStorage([selectedProductUuid]);
    const totalSelectedProductsOnCurrentPage = getTotalUnSelectedProductsOnCurrentPage();
    isSelectAllBtnChecked.value = (refItemsPerPage.value - totalSelectedProductsOnCurrentPage) === refItemsPerPage.value;
}

const onRowUnselect = async (event: any): Promise<void> => {
    const unSelectedProductUuid = event.data.uuid;
    removeUnSelectedProductFromProductsToAddInStorage([unSelectedProductUuid]);
    const productsToAddInStorage = getSelectedProductsToAddFromStorage();
    let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !unSelectedProductUuid.includes(uuid));
    totalSelectedProducts.value = newProductsToAddInStorage.length;
    isSelectAllBtnChecked.value = false;
}

const getCurrentPageSelectedProductsUuids = () => {
    let localStorageData = getLocalStorageData();
    return localStorageData.selection.map(({ uuid }) => uuid);
}

function getSelectedProductsUuids() {
    return getSelectedProductsToAddFromStorage();
}

const getSelectedProductsToAddFromStorage = (): string[] => {
    const selectedProductsToAdd = localStorage.getItem(selectedProductToAddStorageKey);
    return selectedProductsToAdd ? JSON.parse(selectedProductsToAdd) : [];
}

const addSelectedProductInProductsToAddInStorage = (uuids: string[]) => {
    if (uuids.length === 0) {
        totalSelectedProducts.value = 0;
        localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify([]));
        return;
    }

    let productsToAddInStorage = getSelectedProductsToAddFromStorage();
    uuids.forEach(uuid => {
        if (!productsToAddInStorage.includes(uuid)) {
            productsToAddInStorage.push(uuid);
        }
    });
    localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify(productsToAddInStorage));
}

const removeUnSelectedProductFromProductsToAddInStorage = (uuids: string[]) => {
    let productsToAddInStorage = getSelectedProductsToAddFromStorage();
    let newProductsToAddInStorage = productsToAddInStorage.filter(uuid => !uuids.includes(uuid));
    localStorage.setItem(selectedProductToAddStorageKey, JSON.stringify(newProductsToAddInStorage));
}

const getTotalUnSelectedProductsOnCurrentPage = (): number => {
    return productsOfCurrentPage.value
        .filter((item: IProductVariantForGrant) => !getSelectedProductsToAddFromStorage().includes(item.uuid)).length;
};

function getLocalStorageData() {
  let storageData = localStorage.getItem(tableLocalStorageKey);
  if (null !== storageData && "undefined" !== storageData) {
    return JSON.parse(storageData);
  }

  return null;
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
  let storageData = localStorage.getItem(tableLocalStorageKey);
  if (storageData) {
    let localStorageObject = JSON.parse(storageData);
    localStorageObject.rows = rowPerPage;
    localStorage.setItem(tableLocalStorageKey, JSON.stringify(localStorageObject));
  }
}

function getLocalStorageRowsPerPage() {
  let localStorageData = getLocalStorageData();
  if (null !== localStorageData && localStorageData.rows) {
    return localStorageData.rows;
  }

  return defaultRowsPerPage;
}

function getLocalStorageFisrtPageState() {
  let localStorageData = getLocalStorageData();
  if (null !== localStorageData && localStorageData.first) {
    return localStorageData.first;
  }

  return 0;
}
</script>
<style scoped lang="scss">

select {
  font-weight: 400;
}
.card-body {
  background-color: #fff;
}
::v-deep(table tr td:nth-child(2)) {
  cursor: pointer;
}

.formkit-option {
  display: flex;
  align-items: center;
}

.formkit-option img {
  width: 20px;
  height: 20px;
  margin-right: 10px;
}
</style>